import {
  getLocale,
  getMonthName,
  localizePath,
  switchLocale,
  t,
  LOCALES,
  LOCALE_LABELS,
  LOCALE_NATIVE_NAMES,
  type Locale,
} from '../i18n';
import { formatMonthUrl } from '../utils/date';

class AppHeader extends HTMLElement {
  connectedCallback() {
    if (!this.shadowRoot) {
      this.attachShadow({ mode: 'open' });
    }
    this.render();
    this.setupEventListeners();
  }

  private monthLink(date: Date): { url: string; text: string } {
    const monthIndex = date.getMonth();
    const year = date.getFullYear();
    return {
      url: formatMonthUrl(monthIndex, year),
      text: `${getMonthName(monthIndex)} ${year}`,
    };
  }

  private languageSwitcherHtml(): string {
    const current = getLocale();
    const options = LOCALES.map((locale) => {
      const selected = locale === current ? 'aria-current="true" class="lang-option active"' : 'class="lang-option"';
      return `<button type="button" data-locale="${locale}" ${selected} title="${LOCALE_NATIVE_NAMES[locale]}" aria-label="${LOCALE_NATIVE_NAMES[locale]}">${LOCALE_LABELS[locale]}</button>`;
    }).join('<span class="lang-sep" aria-hidden="true">|</span>');

    return `
      <div class="lang-switcher" role="group" aria-label="${t('nav.language')}">
        ${options}
      </div>
    `;
  }

  private render() {
    if (!this.shadowRoot) return;

    const currentDate = new Date();
    const current = this.monthLink(currentDate);

    const nextMonth = new Date(currentDate);
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    const next = this.monthLink(nextMonth);

    const followingMonth = new Date(nextMonth);
    followingMonth.setMonth(followingMonth.getMonth() + 1);
    const following = this.monthLink(followingMonth);

    const homeUrl = localizePath('/');
    const supportUrl = localizePath('/support.html');

    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: block;
          font-family: var(--font-family);
          --header-primary-color: var(--primary-color);
          --header-text-color: var(--text-color);
        }
        
        header {
          text-align: center;
          padding: 0.75rem 0 0.15rem;
          margin-bottom: 0.15rem;
          border-bottom: none;
          position: relative;
        }
        
        .header-content {
          max-width: 600px;
          margin: 0 auto;
          padding: 0 1rem;
        }
        
        .header-left, .header-right {
          flex: 0 0 72px;
          display: flex;
          align-items: center;
        }

        .header-right {
          justify-content: flex-end;
        }
        
        .header-center {
          flex: 1;
          text-align: center;
        }
        
        .header-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.15rem;
          padding-top: 0.35rem;
          position: relative;
        }
        
        .subtitle-row {
          text-align: center;
        }
        
        .site-title {
          font-size: 2.15rem;
          font-weight: var(--font-weight-bold);
          color: var(--header-primary-color);
          margin: 0;
        }
        
        .site-title a {
          color: inherit;
          text-decoration: none;
        }
        
        .site-title a:hover {
          color: inherit;
        }
        
        .site-subtitle {
          font-size: 1.05rem;
          font-weight: var(--font-weight-normal);
          color: var(--header-text-color);
          margin: 0.2rem 0 0;
        }
        
        .burger-menu {
          border: 2px solid var(--header-primary-color);
          border-radius: 4px;
          width: 40px;
          height: 40px;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          gap: 5px;
          cursor: pointer;
          background: transparent;
          padding: 0;
        }
        
        .burger-menu:hover {
          background-color: rgba(37, 99, 235, 0.1);
        }
        
        .burger-menu div {
          width: 20px;
          height: 3px;
          background-color: var(--header-primary-color);
          border-radius: 1px;
          transform-origin: center;
        }

        .lang-switcher {
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
          font-size: 0.85rem;
          font-weight: var(--font-weight-semibold);
        }

        .lang-option {
          background: none;
          border: none;
          color: var(--header-text-color);
          cursor: pointer;
          padding: 0.35rem 0.2rem;
          font: inherit;
          font-weight: var(--font-weight-semibold);
          opacity: 0.55;
        }

        .lang-option:hover {
          color: var(--header-primary-color);
          opacity: 1;
        }

        .lang-option.active {
          color: var(--header-primary-color);
          opacity: 1;
          text-decoration: underline;
          text-underline-offset: 3px;
        }

        .lang-sep {
          color: #cbd5e1;
          user-select: none;
        }
        
        .nav-menu {
          position: fixed;
          top: 0;
          left: -250px;
          width: 250px;
          height: 100vh;
          background-color: white;
          box-shadow: 2px 0 5px rgba(0,0,0,0.1);
          transition: 0.3s;
          z-index: 998;
        }
        
        .nav-menu.active {
          left: 0;
        }
        
        .nav-menu ul {
          list-style: none;
          padding: 2rem;
          margin: 0;
        }
        
        .nav-menu li {
          margin: 1rem 0;
        }
        
        .nav-menu a {
          color: var(--header-text-color);
          text-decoration: none;
          font-size: 1.1rem;
        }
        
        .nav-menu a:hover {
          color: var(--header-primary-color);
        }
        
        @media (max-width: 768px) {
          header {
            padding-left: 0;
          }
        }
        
        @media print {
          header {
            display: none !important;
          }
        }
      </style>
      
      <header>
        <div class="header-content">
          <div class="header-row">
            <div class="header-left">
              <button type="button" class="burger-menu" id="burgerMenu" aria-label="${t('nav.menu')}" aria-expanded="false" aria-controls="navMenu">
                <div></div>
                <div></div>
                <div></div>
              </button>
            </div>
            <div class="header-center">
              <h1 class="site-title"><a href="${homeUrl}">Calendarify</a></h1>
            </div>
            <div class="header-right">
              ${this.languageSwitcherHtml()}
            </div>
          </div>
          <div class="subtitle-row">
            <p class="site-subtitle">${t('header.subtitle')}</p>
          </div>
        </div>
        <nav class="nav-menu" id="navMenu">
          <ul>
            <li><a href="${homeUrl}">${t('nav.home')}</a></li>
            <li><a href="${current.url}">${current.text}</a></li>
            <li><a href="${next.url}">${next.text}</a></li>
            <li><a href="${following.url}">${following.text}</a></li>
            <li><a href="${supportUrl}">${t('nav.support')}</a></li>
          </ul>
        </nav>
      </header>
    `;
  }

  private setupEventListeners() {
    if (!this.shadowRoot) return;

    const burgerMenu = this.shadowRoot.getElementById('burgerMenu');
    const navMenu = this.shadowRoot.getElementById('navMenu');

    if (burgerMenu && navMenu) {
      burgerMenu.addEventListener('click', () => {
        const open = navMenu.classList.toggle('active');
        burgerMenu.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    }

    this.shadowRoot.querySelectorAll<HTMLButtonElement>('[data-locale]').forEach((button) => {
      button.addEventListener('click', () => {
        const locale = button.dataset.locale as Locale;
        switchLocale(locale);
      });
    });

    document.addEventListener('click', (e) => {
      if (navMenu &&
        navMenu.classList.contains('active') &&
        !this.contains(e.target as Node) &&
        e.target !== this) {
        navMenu.classList.remove('active');
        burgerMenu?.setAttribute('aria-expanded', 'false');
      }
    });
  }
}

customElements.define('app-header', AppHeader);
